/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };
  const closeMenu = () => setIsMenuOpen(false);

  // ✅ Apple App Store link
  const handleAppDownload = () => {
    window.open(
      "https://apps.apple.com/in/app/event-planly/id6753165774",
      "_blank",
      "noopener,noreferrer"
    );
  };

  // ✅ Google Play Store link
  const handlePlayStoreClick = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.itdesks.eventsplanly&pcampaignid=web_share",
      "_blank",
      "noopener,noreferrer"
    );
  };

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpenDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="bg-gray-50 px-4 sm:px-6 py-4 relative">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        
        {/* Logo */}
        <Link
          to="/"
          className="text-xl sm:text-2xl font-bold text-gray-900 hover:scale-105 transition-transform duration-300"
        >
          EventPlanly
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
          <Link
            to="/"
            className="text-gray-700 hover:text-orange-500 hover:-translate-y-0.5 transition-all duration-300"
          >
            Home
          </Link>

          {/* ✅ Download Our App Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setOpenDropdown(!openDropdown)}
              className="text-gray-700 hover:text-orange-500 transition-all duration-300 bg-transparent border-none cursor-pointer"
            >
              Download Our App
            </button>

            {openDropdown && (
              <div className="absolute flex flex-col bg-white shadow-lg border border-gray-200 mt-2 p-3 rounded-lg w-52 z-50">
                <button
                  onClick={handleAppDownload}
                  className="flex items-center gap-2 text-sm text-gray-800 hover:text-orange-500 transition-colors py-2 border-b border-gray-100"
                >
                  <img
                    src="https://img.icons8.com/ios-filled/24/000000/mac-os.png"
                    alt="App Store"
                    className="w-5 h-5"
                  />
                  App Store (iOS)
                </button>

                <button
                  onClick={handlePlayStoreClick}
                  className="flex items-center gap-2 text-sm text-gray-800 hover:text-orange-500 transition-colors py-2"
                >
                  <img
                    src="https://img.icons8.com/color/24/000000/google-play.png"
                    alt="Play Store"
                    className="w-5 h-5"
                  />
                  Play Store (Android)
                </button>
              </div>
            )}
          </div>

          <Link
            to="/terms-and-conditions"
            className="text-gray-700 hover:text-orange-500 hover:-translate-y-0.5 transition-all duration-300"
          >
            Terms and Conditions
          </Link>

          <Link
            to="/privacy-policy"
            className="text-gray-700 hover:text-orange-500 hover:-translate-y-0.5 transition-all duration-300"
          >
            Privacy Policy
          </Link>
        </nav>

        {/* Contact & Login Buttons */}
        <div className="hidden sm:flex items-center space-x-4">
          <button
            className="bg-gray-900 text-white px-4 lg:px-6 py-2 rounded-full hover:bg-gray-800 hover:scale-105 active:scale-95 transition-all duration-300"
            onClick={() => handleNavigation("/contact")}
          >
            Contact Us
          </button>
          <button
            className="bg-orange-500 text-white px-4 lg:px-6 py-2 rounded-full hover:bg-orange-600 hover:scale-105 active:scale-95 transition-all duration-300"
            onClick={() => handleNavigation("/login")}
          >
            Login / Signup
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={toggleMenu}
          className="lg:hidden p-2 rounded-md hover:bg-gray-200 transition-colors duration-200"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <X className="h-6 w-6 text-gray-700" />
          ) : (
            <Menu className="h-6 w-6 text-gray-700" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50">
          <nav className="px-4 py-4 space-y-4">
            <Link
              to="/"
              className="block text-gray-700 hover:text-orange-500 py-2 border-b border-gray-100 transition-colors duration-300"
              onClick={closeMenu}
            >
              Home
            </Link>

            {/* Mobile App Links */}
            <button
              onClick={() => {
                handleAppDownload();
                closeMenu();
              }}
              className="block w-full text-left text-gray-700 hover:text-orange-500 py-2 border-b border-gray-100 transition-colors duration-300 bg-transparent"
            >
              Download on App Store (iOS)
            </button>

            <button
              onClick={() => {
                handlePlayStoreClick();
                closeMenu();
              }}
              className="block w-full text-left text-gray-700 hover:text-orange-500 py-2 border-b border-gray-100 transition-colors duration-300 bg-transparent"
            >
              Download on Play Store (Android)
            </button>

            <button
              type="button"
              className="block text-left w-full text-gray-700 hover:text-orange-500 py-2 border-b border-gray-100 transition-colors duration-300 bg-transparent"
              onClick={() => handleNavigation("/terms-and-conditions")}
            >
              Terms and Conditions
            </button>

            <button
              type="button"
              className="block text-left w-full text-gray-700 hover:text-orange-500 py-2 border-b border-gray-100 transition-colors duration-300 bg-transparent"
              onClick={() => handleNavigation("/privacy-policy")}
            >
              Privacy Policy
            </button>

            <div className="flex flex-col gap-3 mt-4">
              <button
                className="w-full bg-gray-900 text-white px-6 py-3 rounded-full hover:bg-gray-800 transition-colors duration-300"
                onClick={() => handleNavigation("/contact")}
              >
                Contact Us
              </button>

              <button
                className="w-full bg-orange-500 text-white px-6 py-3 rounded-full hover:bg-orange-600 transition-colors duration-300"
                onClick={() => handleNavigation("/login")}
              >
                Login / Signup
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
